import { Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PopoverController } from '@ionic/angular';
import { TranslationHelperService } from '../../services/translation-helper.service';
import { LanguageSwitcherPopoverComponent } from './language-switcher-popover/language-switcher-popover.component';

interface Language {
  code: string;
  name: string;
}

@Component({
  selector: 'app-language-switcher',
  template: `
    <ion-button
      *ngIf="showLanguageSwitcher"
      fill="clear"
      (click)="presentPopover($event)"
      class="language-button"
    >
      <ion-icon name="globe-outline" class="globe-icon"></ion-icon>
      <span class="language-name">{{
        currentLanguage.code.toUpperCase()
      }}</span>
      <ion-icon name="chevron-down" class="chevron-icon"></ion-icon>
    </ion-button>
  `,
  styles: [
    `
      .language-button {
        --padding-start: 12px;
        --padding-end: 12px;
        height: 36px;
        margin: 0;
        display: flex;
        align-items: center;
        gap: 8px;
        border-radius: 18px;
        background: var(--ion-color-light);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        font-weight: 500;
        --color: var(--ion-color-dark);
      }

      .language-button:hover {
        background: var(--ion-color-light-shade);
      }

      .globe-icon {
        margin-right: 4px;
        font-size: 18px;
        color: var(--ion-color-dark);
      }

      .language-name {
        font-size: 14px;
        font-weight: 500;
      }

      .chevron-icon {
        font-size: 16px;
        margin-left: 4px;
        color: var(--ion-color-dark);
      }

      @media (max-width: 768px) {
        .language-name {
          display: none;
        }

        .language-button {
          --padding-start: 8px;
          --padding-end: 8px;
          gap: 4px;
        }

        .globe-icon {
          margin-right: 0;
        }

        .chevron-icon {
          margin-left: 0;
        }
      }
    `,
  ],
})
export class LanguageSwitcherComponent {
  currentLang: string;
  languages: Language[] = [];

  get currentLanguage(): Language {
    return (
      this.languages.find((lang) => lang.code === this.currentLang) || {
        code: this.currentLang,
        name: this.currentLang.toUpperCase(),
      }
    );
  }

  get showLanguageSwitcher(): boolean {
    return this.languages.length > 1 && this.translationHelper.isTranslationEnabled();
  }

  constructor(
    private translate: TranslateService,
    private popoverController: PopoverController,
    private translationHelper: TranslationHelperService
  ) {
    this.loadLanguageData();
  }

  private loadLanguageData() {
    this.currentLang = this.translationHelper.getCurrentLanguage();

    this.languages = this.translationHelper.getAvailableLanguagesWithNames();

    if (this.languages.length === 0) {
      this.languages = [{ code: 'en', name: 'English' }];
    }
  }

  async presentPopover(ev: any) {
    const popover = await this.popoverController.create({
      component: LanguageSwitcherPopoverComponent,
      event: ev,
      translucent: true,
    });
    await popover.present();

    const { data } = await popover.onDidDismiss();
    if (data) {
      this.currentLang = data;
      this.loadLanguageData();
    }
  }
}